import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { tenantFetch } from '../lib/api-client';
import { useTenant } from '../lib/tenant-context';
import { RequirePlan } from '../components/RouteGuard';
import { Key } from "@solar-icons/react";

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string;
  status: 'active' | 'revoked';
  rate_limit_per_minute: number;
  last_used_at: string | null;
  created_at: string;
}

interface NewKeyResponse {
  id: string;
  name: string;
  key: string;
  keyPrefix: string;
  scopes: string[];
  message: string;
}

const AVAILABLE_SCOPES = [
  { id: 'vision:read', label: 'Vision API', description: 'Analyze product images' },
  { id: 'value:read', label: 'Value API', description: 'Get price estimates' },
  { id: 'products:read', label: 'Products (Read)', description: 'Read product data' },
  { id: 'products:write', label: 'Products (Write)', description: 'Enrich and update products' },
  { id: 'bridge:write', label: 'Bridge (Write)', description: 'Send events from external systems' },
  { id: '*', label: 'Full Access', description: 'All API permissions' },
];

export const Route = createFileRoute('/api-keys')({
  component: () => (
    <RequirePlan required="starter" featureName="API Keys" icon={<Key size={64} weight="LineDuotone" />}>
      <ApiKeys />
    </RequirePlan>
  ),
});

function ApiKeys() {
  const { currentTenant } = useTenant();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState<NewKeyResponse | null>(null);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read', 'vision']);
  
  // Fetch keys
  useEffect(() => {
    fetchKeys();
  }, [currentTenant.id]);
  
  async function fetchKeys() {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantFetch('/api/keys');
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.keys && data.keys.length > 0) {
            setKeys(data.keys);
            setLoading(false);
            return;
          }
        }
      }
      // Use tenant-specific demo data
      setKeys(getTenantDemoKeys(currentTenant.id));
    } catch (err) {
      console.debug('Failed to fetch keys, using demo data');
      setKeys(getTenantDemoKeys(currentTenant.id));
    } finally {
      setLoading(false);
    }
  }
  
  // Tenant-specific demo API keys
  function getTenantDemoKeys(tenantId: string): ApiKey[] {
    const now = new Date();
    const dayAgo = new Date(now.getTime() - 86400000);
    const weekAgo = new Date(now.getTime() - 604800000);
    
    if (tenantId === 'arcteryx') {
      return [
        {
          id: 'key-arc-1',
          name: 'Arc\'teryx Production',
          key_prefix: 'arc_prod_sk_',
          scopes: 'vision:read,value:read,products:read,products:write',
          status: 'active',
          rate_limit_per_minute: 1000,
          last_used_at: dayAgo.toISOString(),
          created_at: weekAgo.toISOString(),
        },
        {
          id: 'key-arc-2',
          name: 'Arc\'teryx Development',
          key_prefix: 'arc_dev_sk_',
          scopes: 'vision:read,value:read',
          status: 'active',
          rate_limit_per_minute: 100,
          last_used_at: null,
          created_at: weekAgo.toISOString(),
        },
        {
          id: 'key-arc-3',
          name: 'ReBIRD Integration',
          key_prefix: 'arc_rebird_',
          scopes: 'bridge:write,products:read',
          status: 'active',
          rate_limit_per_minute: 500,
          last_used_at: new Date(now.getTime() - 3600000).toISOString(),
          created_at: new Date(now.getTime() - 2592000000).toISOString(),
        },
      ];
    } else if (tenantId === 'nespresso') {
      return [
        {
          id: 'key-nes-1',
          name: 'Nespresso Production',
          key_prefix: 'nes_prod_sk_',
          scopes: '*',
          status: 'active',
          rate_limit_per_minute: 1000,
          last_used_at: dayAgo.toISOString(),
          created_at: weekAgo.toISOString(),
        },
      ];
    }
    
    // Default: Lululemon
    return [
      {
        id: 'key-lulu-1',
        name: 'lululemon Production',
        key_prefix: 'lulu_prod_sk_',
        scopes: 'vision:read,value:read,products:read',
        status: 'active',
        rate_limit_per_minute: 1000,
        last_used_at: dayAgo.toISOString(),
        created_at: weekAgo.toISOString(),
      },
      {
        id: 'key-lulu-2',
        name: 'lululemon Test',
        key_prefix: 'lulu_test_sk_',
        scopes: 'vision:read',
        status: 'active',
        rate_limit_per_minute: 100,
        last_used_at: null,
        created_at: weekAgo.toISOString(),
      },
    ];
  }
  
  async function createKey() {
    if (!keyName.trim()) return;
    
    setCreating(true);
    setError(null);
    
    try {
      const response = await tenantFetch('/api/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: keyName,
          scopes: selectedScopes,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create key');
      }
      
      const data: NewKeyResponse = await response.json();
      setNewKey(data);
      fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  }
  
  async function revokeKey(id: string) {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await tenantFetch(`/api/keys/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to revoke key');
      fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  }
  
  function closeModal() {
    setShowCreateModal(false);
    setNewKey(null);
    setKeyName('');
    setSelectedScopes(['read', 'vision']);
  }
  
  function toggleScope(scope: string) {
    setSelectedScopes(prev => 
      prev.includes(scope) 
        ? prev.filter(s => s !== scope)
        : [...prev, scope]
    );
  }
  
  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>API Keys</h1>
          <p className="text-base mt-1" style={{ color: '#A9A9A9' }}>
            Manage keys for Vision, Value, and Product Intelligence APIs
          </p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          Create API Key
        </button>
      </div>
      
      {/* API Endpoints Info */}
      <div className="p-6 " style={{ backgroundColor: '#171717' }}>
        <h3 className="font-light mb-4" style={{ color: '#FFFFFF' }}>API Endpoints</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-mono text-xs" style={{ color: '#00FBA9' }}>VISION API</p>
            <p className="mt-1" style={{ color: '#A9A9A9' }}>Product identification from images</p>
            <code className="text-xs mt-2 block" style={{ color: '#FFFFFF' }}>POST /v1/vision/identify</code>
          </div>
          <div>
            <p className="font-mono text-xs" style={{ color: '#00FBA9' }}>VALUE API</p>
            <p className="mt-1" style={{ color: '#A9A9A9' }}>Pricing and valuation estimates</p>
            <code className="text-xs mt-2 block" style={{ color: '#FFFFFF' }}>POST /v1/value/estimate</code>
          </div>
          <div>
            <p className="font-mono text-xs" style={{ color: '#00FBA9' }}>PRODUCT INTELLIGENCE</p>
            <p className="mt-1" style={{ color: '#A9A9A9' }}>Semantic search and enrichment</p>
            <code className="text-xs mt-2 block" style={{ color: '#FFFFFF' }}>POST /v1/products/search</code>
          </div>
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="p-4 " style={{ backgroundColor: 'rgba(255,76,0,0.1)', color: '#FF4C00' }}>
          {error}
        </div>
      )}
      
      {/* Keys Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead style={{ backgroundColor: '#FAFAFA' }}>
            <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
              <th className="px-6 py-3 text-left text-xs font-light uppercase tracking-wider" style={{ color: '#A9A9A9' }}>
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-light uppercase tracking-wider" style={{ color: '#A9A9A9' }}>
                Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-light uppercase tracking-wider" style={{ color: '#A9A9A9' }}>
                Scopes
              </th>
              <th className="px-6 py-3 text-left text-xs font-light uppercase tracking-wider" style={{ color: '#A9A9A9' }}>
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-light uppercase tracking-wider" style={{ color: '#A9A9A9' }}>
                Created
              </th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4" colSpan={6}>
                    <div className="h-8 rounded animate-pulse" style={{ backgroundColor: '#FAFAFA' }} />
                  </td>
                </tr>
              ))
            ) : keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div style={{ color: '#A9A9A9' }}>
                    <p className="mb-2">No API keys yet</p>
                    <p className="text-sm mb-4">Create your first key to start making API calls</p>
                    <button 
                      onClick={() => setShowCreateModal(true)}
                      className="btn-primary"
                    >
                      Create API Key
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              keys.map(key => (
                <tr key={key.id} className={key.status === 'revoked' ? 'opacity-50' : ''}>
                  <td className="px-6 py-4">
                    <p className="font-light" style={{ color: '#171717' }}>{key.name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm px-2 py-1 rounded" style={{ backgroundColor: '#FAFAFA', color: '#A9A9A9' }}>
                      {key.key_prefix}...
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {key.scopes.split(',').map(scope => (
                        <span 
                          key={scope}
                          className="px-2 py-0.5 rounded-full text-xs font-mono uppercase"
                          style={{ backgroundColor: 'rgba(0,251,169,0.15)', color: '#00FBA9' }}
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm" style={{ color: '#A9A9A9' }}>
                      {key.last_used_at ? formatDate(key.last_used_at) : 'Never used'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm" style={{ color: '#A9A9A9' }}>
                      {formatDate(key.created_at)}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {key.status === 'active' ? (
                      <button 
                        onClick={() => revokeKey(key.id)}
                        className="text-sm transition-colors"
                        style={{ color: '#FF4C00' }}
                      >
                        Revoke
                      </button>
                    ) : (
                      <span className="text-sm" style={{ color: '#A9A9A9' }}>Revoked</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(23,23,23,0.5)' }}>
          <div className="card max-w-lg w-full mx-4">
            {newKey ? (
              // Success - show key
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,251,169,0.15)' }}>
                    <span style={{ color: '#00FBA9' }}>✓</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-light" style={{ color: '#171717' }}>API Key Created</h2>
                    <p className="text-sm" style={{ color: '#A9A9A9' }}>{newKey.name}</p>
                  </div>
                </div>
                
                <div className="p-4  mb-4" style={{ backgroundColor: '#FAFAFA' }}>
                  <p className="text-xs mb-2 font-mono uppercase" style={{ color: '#A9A9A9' }}>Your API Key</p>
                  <div className="flex items-center gap-2">
                    <div 
                      className="flex-1 p-3  text-sm font-mono break-all select-all"
                      style={{ backgroundColor: '#171717', color: '#00FBA9' }}
                    >
                      {newKey.key}
                    </div>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(newKey.key)}
                    className="btn-primary w-full mt-3"
                  >
                    Copy to Clipboard
                  </button>
                </div>
                
                <div className="p-4  mb-6" style={{ backgroundColor: 'rgba(255,76,0,0.1)' }}>
                  <p className="text-sm" style={{ color: '#FF4C00' }}>
                    Save this key now — it won't be shown again.
                  </p>
                </div>
                
                <button onClick={closeModal} className="btn-primary w-full">
                  Done
                </button>
              </>
            ) : (
              // Create form
              <>
                <h2 className="text-lg font-light mb-4" style={{ color: '#171717' }}>Create API Key</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#A9A9A9' }}>Name</label>
                    <input
                      type="text"
                      value={keyName}
                      onChange={e => setKeyName(e.target.value)}
                      placeholder="e.g., Production API"
                      className="w-full px-4 py-2 border "
                      style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#A9A9A9' }}>Scopes</label>
                    <div className="space-y-2">
                      {AVAILABLE_SCOPES.map(scope => (
                        <label 
                          key={scope.id} 
                          className="flex items-start gap-3 p-3  cursor-pointer transition-colors"
                          style={{ 
                            backgroundColor: selectedScopes.includes(scope.id) ? 'rgba(0,251,169,0.1)' : '#FAFAFA'
                          }}
                        >
                          <input 
                            type="checkbox"
                            checked={selectedScopes.includes(scope.id)}
                            onChange={() => toggleScope(scope.id)}
                            className="mt-1"
                          />
                          <div>
                            <p className="font-mono text-sm uppercase" style={{ color: '#171717' }}>{scope.label}</p>
                            <p className="text-xs" style={{ color: '#A9A9A9' }}>{scope.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button onClick={closeModal} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button 
                    onClick={createKey}
                    disabled={!keyName.trim() || creating}
                    className="btn-primary flex-1"
                  >
                    {creating ? 'Creating...' : 'Create Key'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return date.toLocaleDateString();
}
